import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import UserCard from 'flarum/forum/components/UserCard';

// These 5 attributes are added to User by this same extension's own
// extend.php/extend.ts (Extend\Model / Extend.Model(User).attribute(...)) -
// real at runtime, but each extension is its own TypeScript compilation
// unit, so every file across every extension that reads one of these
// needs its own copy of this augmentation (see also
// champis-species/authorDisplayName.ts's champisDisplayName-only one).

/**
 * Shows the five optional profile fields (see addRegistrationFields.js /
 * addProfileSettings.js) on UserCard - only when set, per field. UserCard
 * is the *one* component behind both places this needs to show up: it's
 * used verbatim both as the profile page's hero (UserPage.hero()) and as
 * the popover CommentPost shows when hovering a post author's name/avatar
 * (see Comment.tsx's `cardVisible` branch) - so extending it here covers
 * both surfaces without any separate hover-specific code. (That hover
 * popover only exists for post authors inside an open discussion today -
 * core doesn't wire up the same hover behaviour for usernames in the
 * discussion list.)
 *
 * Prénom/Nom go in contentItems (a subtitle under the username, alongside
 * identity/badges/info) since a real name reads as more prominent than a
 * tidbit; Localisation/Site web perso go in infoItems (the small
 * lastSeen/joined-style line); Association(s) gets its own contentItems
 * entry since a list of links doesn't fit the single-line infoItems shape.
 */
export default function addProfileDisplay() {
  extend(UserCard.prototype, 'contentItems', function (items) {
    const user = this.attrs.user;
    const fullName = [user.champisFirstName(), user.champisLastName()].filter(Boolean).join(' ');
    if (fullName) {
      items.add('champisFullName', m("p", {
        className: "UserCard-champisFullName"
      }, fullName), 95);
    }
    const associations = user.champisAssociations() || [];
    if (associations.length) {
      items.add('champisAssociations', m("ul", {
        className: "UserCard-champisAssociations"
      }, associations.map(association => m("li", {
        key: association.name + association.url
      }, m("i", {
        className: "icon fas fa-users",
        "aria-hidden": "true"
      }), ' ', association.url ? m("a", {
        href: association.url,
        target: "_blank",
        rel: "noopener noreferrer",
        onclick: e => e.stopPropagation()
      }, association.name || association.url) : association.name))), 75);
    }
  });
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;
    if (user.champisLocation()) {
      items.add('champisLocation', m("span", {
        className: "UserCard-champisLocation"
      }, m("i", {
        className: "icon fas fa-map-marker-alt",
        "aria-hidden": "true"
      }), " ", user.champisLocation()), 70);
    }

    // The age only, as on phpBB's profiles: the date of birth itself is
    // only its owner's to see (see extend.php).
    if (typeof user.champisAge() === 'number') {
      items.add('champisAge', m("span", {
        className: "UserCard-champisAge"
      }, m("i", {
        className: "icon fas fa-birthday-cake",
        "aria-hidden": "true"
      }), ' ', app.translator.trans('champis-users.forum.profile.age', {
        count: user.champisAge()
      })), 65);
    }
    if (user.champisWebsite()) {
      items.add('champisWebsite', m("span", {
        className: "UserCard-champisWebsite"
      }, m("i", {
        className: "icon fas fa-globe",
        "aria-hidden": "true"
      }), ' ', m("a", {
        href: user.champisWebsite(),
        target: "_blank",
        rel: "noopener noreferrer",
        onclick: e => e.stopPropagation()
      }, user.champisWebsite())), 60);
    }
  });
}